import type { CreateClientDetailsDto } from '@nestjs-boilerplate/types';
import type { UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

interface AddressFieldsProps {
  form: UseFormReturn<CreateClientDetailsDto>;
  prefix?: string;
}

export function AddressFields({ form, prefix = '' }: AddressFieldsProps) {
  // Build field names with optional prefix (e.g., "companies.0.")
  const fieldName = (name: string) =>
    (prefix ? `${prefix}${name}` : name) as keyof CreateClientDetailsDto;

  return (
    <div className="space-y-6">
      {/* Postcode with Find Address button */}
      <div className="flex gap-4">
        <FormField
          control={form.control}
          name={fieldName('postcode')}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>Postcode</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter postcode"
                  {...field}
                  value={(field.value as string) ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex items-end">
          <Button
            type="button"
            disabled
            className="h-10 bg-[#131313] px-6 text-white hover:bg-[#131313]/90"
          >
            Find Address
          </Button>
        </div>
      </div>

      {/* Address fields */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField
          control={form.control}
          name={fieldName('addressLine1')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address Line 1</FormLabel>
              <FormControl>
                <Input
                  placeholder="Street address"
                  {...field}
                  value={(field.value as string) ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={fieldName('addressLine2')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address Line 2 (Optional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="Apartment, suite, etc."
                  {...field}
                  value={(field.value as string) ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={fieldName('townCity')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Town/City</FormLabel>
              <FormControl>
                <Input
                  placeholder="Town or city"
                  {...field}
                  value={(field.value as string) ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={fieldName('county')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>County (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="County" {...field} value={(field.value as string) ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
